import { useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import { Button, Table, Drawer, Form, Input, Select, message, Popconfirm, } from "antd"
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, } from "@ant-design/icons"
import { translations } from "../../utils/constants"
import { secureApi } from "../../Config/api"
import { POST, GET } from "../../utils/apis"
import { useDispatch, useSelector } from "react-redux"
import { setAllUsers, setUserPage, setUserPageSize, setUserTotal, } from "../../Redux/actions/userAction"
import { verifyToken } from "../../utils/helpers"
import paths from "../../Config/paths"

const { Option } = Select

const Users = ({ language }) => {
    const history = useHistory()
    const dispatch = useDispatch()
    const user = useSelector((state) => state.authReducer?.user)
    const { users, page, pageSize, totalItems } = useSelector(
        (state) => state.users
    )
    const authUser = useSelector((state) => state.authReducer?.user)

    const [drawerVisible, setDrawerVisible] = useState(false)
    const [form] = Form.useForm()
    const [editingUser, setEditingUser] = useState(null)
    const [search, setSearch] = useState("")

    useEffect(() => {
        if (user && !['admin', 'superAdmin'].includes(user.role)) {
            history.push(paths?.HOME)
        }
    }, [user, history])


    useEffect(() => {

        fetchUsers()
    }, [page, pageSize, search])

    useEffect(() => {
        verifyToken(history, dispatch)
    }, [])

    const fetchUsers = async () => {
        try {
            const params = { page, limit: pageSize, search, userId: authUser?.id }
            const response = await secureApi?.get(GET?.GET_USERS, { params })
            if (response?.success) {
                const mapped = response?.users?.map((u) => ({
                    key: u.id,
                    id: u.id,
                    name: u.name || u.user_name,
                    email: u.email,
                    role: u.role,
                }))

                dispatch(setAllUsers(mapped))
                dispatch(setUserTotal(response?.total || mapped.length))
            } else {
                // message.error(response?.message)
                console.log("Failed to fetch users:", response?.message)
            }
        } catch (err) {
            message.error(translations[language]?.fetchError)
        }
    }

    console.log('user=====>', user)

    const handleAddUser = async (values) => {
        try {
            const payload = {
                username: values.name,
                email: values.email,
                password: values.password,
                role: values.role,
                adminId: user?.id
            }
            const response = await secureApi?.post(POST?.ADD_USER, payload)

            if (response?.success) {
                message.success(translations[language]?.userAdded)
                fetchUsers()
                form.resetFields()
                setDrawerVisible(false)
            } else {
                message.error(response?.message)
            }
        } catch (error) {
            message.error(translations[language]?.addUserError)
        }
    }

    const handleEditUser = async (values) => {
        try {
            const payload = {
                id: editingUser.id,
                username: values.name,
                email: editingUser.email,
                role: values.role,
            }

            const response = await secureApi.post(POST.EDIT_USER, payload)

            if (response?.success) {
                message.success(translations[language]?.userUpdated)
                fetchUsers()
                form.resetFields()
                setEditingUser(null)
                setDrawerVisible(false)
            } else {
                message.error(response?.message)
            }
        } catch (error) {
            message.error(translations[language]?.updateUserError)
        }
    }

    const handleRoleChange = async (key, newRole) => {
        try {
            const user = users.find((u) => u.key === key)
            if (!user) return

            const payload = {
                id: user.id,
                username: user.name,
                email: user.email,
                role: newRole,
            }

            const response = await secureApi.post(POST.EDIT_USER, payload)
            if (response?.success) {
                message.success(translations[language]?.roleUpdated)
                fetchUsers()
            } else {
                message.error(response?.message)
            }
        } catch (err) {
            message.error(translations[language]?.updateRoleError)
        }
    }

    const handleDelete = async (id) => {
        try {
            const response = await secureApi.post(POST.DELETE_USER, { id })
            if (response?.success) {
                message.success("User deleted successfully!")
                fetchUsers()
            } else {
                message.error(response?.message || "Failed to delete user")
            }
        } catch (err) {
            message.error("Error deleting user")
        }
    }

    const handleCancel = () => {
        form.resetFields()
        setEditingUser(null)
        setDrawerVisible(false)
    }

    const columns = [
        { title: translations[language]?.name, dataIndex: "name" },
        { title: translations[language]?.email, dataIndex: "email" },
        {
            title: translations[language]?.role,
            dataIndex: "role",
            render: (role, record) => {
                const isCurrentUser = record.id === authUser?.id
                return (
                    <Select
                        value={role}
                        style={{ width: 120 }}
                        onChange={(value) => handleRoleChange(record.key, value)}
                        disabled={isCurrentUser}
                    >
                        <Option value="admin">{translations[language]?.admin}</Option>
                        <Option value="user">{translations[language]?.user}</Option>
                        <Option value="qualityUser">{translations[language]?.qualityUser}</Option>
                        <Option value="teamTrack"> {translations[language]?.teamTrack || "Team Track"}</Option>
                    </Select>
                )
            },
        },
        {
            title: translations[language]?.actions,
            render: (_, record) => {
                const isCurrentUser = record.id === authUser?.id
                return (
                    <div style={{ display: "flex", gap: 12 }}>
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            disabled={isCurrentUser}
                            onClick={() => {
                                setEditingUser(record)
                                setDrawerVisible(true)
                                form.setFieldsValue({
                                    name: record.name,
                                    email: record.email,
                                    role: record.role,
                                })
                            }}
                        />
                        <Popconfirm
                            title="Are you sure to delete this user?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                            disabled={isCurrentUser}
                        >
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={isCurrentUser}
                            />
                        </Popconfirm>
                    </div>
                )
            },
        },
    ]

    return (
        <div style={{ padding: 24 }}>
            <div
                className="home-header"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "1rem",
                    alignItems: "center",
                }}
            >
                <div>
                    <h1 className="timesheet-title">
                        {translations[language]?.users}
                    </h1>
                    <p className="timesheet-subtitle">
                        {translations[language]?.userSubtitle}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                    <Input
                        placeholder={translations[language]?.searchUser}
                        prefix={<SearchOutlined />}
                        className="antd-search-input"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)
                            dispatch(setUserPage(1))
                        }}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        className="export-btn"
                        onClick={() => {
                            setEditingUser(null)
                            form.resetFields()
                            setDrawerVisible(true)
                        }}
                    >
                        {translations[language]?.addUser}
                    </Button>
                </div>
            </div>

            <div style={{ marginTop: 32 }}>
                <Table
                    columns={columns}
                    dataSource={users}
                    rowKey="key"
                    scroll={{ x: 'max-content' }}
                    className="custom-timesheet-table"
                    pagination={{
                        current: page,
                        pageSize,
                        total: totalItems,
                        showSizeChanger: true,
                        onChange: (p, size) => {
                            dispatch(setUserPage(p))
                            dispatch(setUserPageSize(size))
                        },
                    }}
                />
            </div>

            <Drawer
                title={
                    editingUser
                        ? translations[language]?.editUser
                        : translations[language]?.addUser
                }
                width={720}
                open={drawerVisible}
                onClose={handleCancel}
                destroyOnClose
                footer={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "8px",
                        }}
                    >
                        <Button onClick={handleCancel} className="cancel-button">
                            {translations[language]?.cancel}
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => form.submit()}
                            className="submit-button"
                        >
                            {editingUser
                                ? translations[language]?.updateUser
                                : translations[language]?.addUser}
                        </Button>
                    </div>
                }
            >
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={editingUser ? handleEditUser : handleAddUser}
                >
                    <Form.Item
                        label={translations[language]?.name}
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: translations[language]?.nameReq,
                            },
                        ]}
                    >
                        <Input
                            className="antd-input"
                            placeholder={translations[language]?.enterName}
                        />
                    </Form.Item>

                    <Form.Item
                        label={translations[language]?.email}
                        name="email"
                        rules={[
                            {
                                required: true,
                                message: translations[language]?.emailReq,
                            },
                        ]}
                    >
                        <Input
                            type="email"
                            placeholder={translations[language]?.enterEmail}
                            className="antd-input"
                            disabled={!!editingUser}
                        />
                    </Form.Item>

                    {!editingUser && (
                        <Form.Item
                            label={translations[language]?.password}
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    message: translations[language]?.passwordReq,
                                },
                            ]}
                        >
                            <Input.Password
                                className="antd-input"
                                placeholder={translations[language]?.enterPassword}
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        label={translations[language]?.role}
                        name="role"
                        rules={[
                            {
                                required: true,
                                message: translations[language]?.roleReq,
                            },
                        ]}
                    >
                        <Select
                            placeholder={translations[language]?.selectRole}
                            className="antd-select"
                        >
                            <Option value="admin">
                                {translations[language]?.admin}
                            </Option>
                            <Option value="user">
                                {translations[language]?.user}
                            </Option>
                            <Option value="qualityUser">
                                {translations[language]?.qualityUser}
                            </Option>

                            <Option value="teamTrack">
                                {translations[language]?.teamTrack || "Team Track"}
                            </Option>

                        </Select>
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    )
}

export default Users
